// Kept out of useRotationCellSave so it can be unit-tested without pulling in
// the service layer (axios) that the hook's saves depend on.

const seasonStartIso = (season: number) => `${season}-01-01T00:00:00.000Z`;

/** The calendar day of an ISO date, for comparing dates saved at different times of day. */
const dayOf = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : null);

interface ResolvePlanStartDateArgs {
  season: number;
  /** The plan's start date now, or null for a plan not yet created. */
  currentStart: string | null;
  /** The date of the plan's one Plan sowing record before this save, if any. */
  previousSowingDate: string | null;
  /** The sowing date being saved, if any. */
  sowingDate: string | null;
}

/**
 * The start date a plan should have after a Rotation save, or null to keep
 * the one it has (OP-2501). A start date mainly places a plan in its year,
 * and advisers read it as the sowing date (as in the legacy app), so it
 * follows the sowing date. The sowing date always falls in the plan's year,
 * so this never moves a plan to another year.
 *
 * - A new plan starts on its sowing date, or 1 January without one.
 * - An existing plan's start date moves only while it's still the 1 January
 *   default or still equal to the previous sowing date — a start date set
 *   by hand in Planning is kept. Clearing the sowing date leaves it alone.
 */
export const resolvePlanStartDate = ({
  season,
  currentStart,
  previousSowingDate,
  sowingDate,
}: ResolvePlanStartDateArgs): string | null => {
  if (currentStart == null) return sowingDate ?? seasonStartIso(season);
  if (!sowingDate || dayOf(currentStart) === dayOf(sowingDate)) return null;
  const followsSowing =
    dayOf(currentStart) === dayOf(seasonStartIso(season)) ||
    (previousSowingDate != null &&
      dayOf(currentStart) === dayOf(previousSowingDate));
  return followsSowing ? sowingDate : null;
};
